import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpenIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

interface DailyReflectionCardProps {
  quote?: string;
  reference?: string;
  prompt?: string;
  isCompleted?: boolean;
}

// Sample daily reflections (in production, these would come from the database)
const dailyReflections = [
  {
    quote: "We are going to know a new freedom and a new happiness.",
    reference: "Big Book, Page 83",
    prompt: "What does freedom mean to you in your recovery today?",
  },
  {
    quote: "We will not regret the past nor wish to shut the door on it.",
    reference: "Big Book, Page 83",
    prompt: "How has your past shaped who you are becoming?",
  },
  {
    quote: "No matter how far down the scale we have gone, we will see how our experience can benefit others.",
    reference: "Big Book, Page 84",
    prompt: "How might your story help someone else today?",
  },
  {
    quote: "We will suddenly realize that God is doing for us what we could not do for ourselves.",
    reference: "Big Book, Page 84",
    prompt: "What changes have you noticed that you couldn't have made alone?",
  },
];

function getTodaysReflection() {
  const dayOfYear = Math.floor(
    (new Date().getTime() - new Date(new Date().getFullYear(), 0, 0).getTime()) /
      (1000 * 60 * 60 * 24)
  );
  return dailyReflections[dayOfYear % dailyReflections.length];
}

export function DailyReflectionCard({
  quote,
  reference,
  prompt,
  isCompleted = false,
}: DailyReflectionCardProps) {
  const todaysReflection = getTodaysReflection();
  const displayQuote = quote || todaysReflection.quote;
  const displayReference = reference || todaysReflection.reference;
  const displayPrompt = prompt || todaysReflection.prompt;

  return (
    <motion.div
      className="bg-white dark:bg-surface-800 rounded-xl border border-surface-200 dark:border-surface-700 overflow-hidden"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-surface-100 dark:border-surface-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
            <BookOpenIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-semibold text-surface-800 dark:text-white">Today's Reflection</h3>
            <p className="text-xs text-surface-500 dark:text-surface-400">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
        {isCompleted && (
          <span className="text-xs font-medium text-accent-600 dark:text-accent-400 bg-accent-50 dark:bg-accent-900/20 px-2 py-1 rounded-full">
            Completed
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <blockquote className="mb-4">
          <p className="font-serif text-lg italic text-surface-700 dark:text-surface-200 leading-relaxed">
            "{displayQuote}"
          </p>
          <footer className="mt-2 text-sm text-surface-500 dark:text-surface-400">
            — {displayReference}
          </footer>
        </blockquote>

        <div className="bg-surface-50 dark:bg-surface-900/50 rounded-lg p-4 mb-4">
          <p className="text-sm font-medium text-surface-600 dark:text-surface-300">
            Reflection prompt:
          </p>
          <p className="text-surface-700 dark:text-surface-200 mt-1">{displayPrompt}</p>
        </div>

        <Link
          to="/journal"
          className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors"
        >
          {isCompleted ? 'View Response' : 'Write Reflection'}
          <ArrowRightIcon className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}
